import { Sparkles, PencilRuler, Route, Sun } from "lucide-react";
import { useSite } from "../lib/SiteContext";
import Seo from "../components/Seo";
import FadeIn from "../components/FadeIn";
import EnquiryForm from "../components/EnquiryForm";

const STEPS = [
  { icon: PencilRuler, title: "Tell us your wishes", text: "Beaches, villages, food, history, photography — whatever you love, in the form below or on WhatsApp." },
  { icon: Route, title: "We design your route", text: "We come back with a suggested plan, timings and insider stops shaped around you." },
  { icon: Sun, title: "Enjoy your day", text: "Your private tour runs at your pace — linger where you love, skip what you don't." },
];

const Customize = () => {
  const { content } = useSite();

  return (
    <div data-testid="customize-page">
      <Seo
        title="Customize Your Private Corfu Tour — Aloha Corfu Tours"
        description="Design your perfect private day in Corfu: tell us what you love and we create a tailored island tour at your pace. Prices on request."
      />
      <section className="bg-pine-dark py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-seafoam">Personalized tours</p>
            <h1 className="mt-2 font-display text-4xl font-semibold text-white sm:text-5xl">
              {content?.custom_title || "Create your own Corfu experience"}
            </h1>
            <p className="mt-4 max-w-2xl text-base text-white/75">
              {content?.custom_text || "Tell us what you love and we design a private tour around you."}
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-5 lg:px-8">
          <div className="lg:col-span-2">
            <FadeIn>
              <h2 className="font-display text-2xl font-semibold text-pine">How it works</h2>
              <div className="mt-6 space-y-5">
                {STEPS.map((s, i) => (
                  <div key={s.title} className="flex gap-4" data-testid={`customize-step-${i}`}>
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-seafoam-light">
                      <s.icon className="h-5 w-5 text-pine" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-pine">{s.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-gray-500">{s.text}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 rounded-2xl bg-sand p-5">
                <p className="flex items-center gap-2 text-sm font-semibold text-pine">
                  <Sparkles className="h-4 w-4 text-gold" /> Ideas to get you started
                </p>
                <ul className="mt-3 space-y-1.5 text-sm text-gray-600">
                  <li>· A beach-hopping day with swimming stops and a seaside lunch</li>
                  <li>· Villages, viewpoints and a slow taverna afternoon</li>
                  <li>· History and culture: Old Town, palaces and museums</li>
                  <li>· A romantic golden-hour route ending at sunset</li>
                </ul>
              </div>
            </FadeIn>
          </div>
          <FadeIn delay={0.1} className="lg:col-span-3">
            <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-lift sm:p-8" data-testid="customize-form-card">
              <h2 className="font-display text-2xl font-semibold text-pine">Send us your wishes</h2>
              <p className="mb-6 mt-1 text-sm text-gray-500">We reply personally with a suggested plan and quote. No payment online.</p>
              <EnquiryForm type="custom" />
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default Customize;
